const SELECTOR_TABS = '.wp-block-ssm-tabs';
const SELECTOR_PANEL = '.wp-block-ssm-tabs__panel';
const SELECTOR_NAV_ITEM = '.wp-block-ssm-tabs__nav-item';
const SELECTOR_ANCHOR = 'a[href^="#"]';

const DATA_ACTIVE_CLASSES = 'activeClasses';
const DATA_INACTIVE_CLASSES = 'inactiveClasses';

const ACTIVATION_KEYS = [
    'Enter',
    ' ',
];

const parseClasses = (value = '') => value.split(' ').filter(Boolean);

const getTargetId = (navItem) => {
    const directTarget = navItem.getAttribute('aria-controls');
    if (directTarget) return directTarget;

    const anchor = navItem.querySelector(SELECTOR_ANCHOR);
    const href = anchor?.getAttribute('href');

    return href?.startsWith('#') ? href.slice(1) : null;
};

const setNavState = (navItem, isActive) => {
    if (!navItem) return;

    const activeClasses = parseClasses(navItem.dataset[DATA_ACTIVE_CLASSES]);
    const inactiveClasses = parseClasses(navItem.dataset[DATA_INACTIVE_CLASSES]);

    activeClasses.forEach((cls) => navItem.classList.toggle(cls, isActive));
    inactiveClasses.forEach((cls) => navItem.classList.toggle(cls, !isActive));
    navItem.setAttribute('aria-selected', isActive ? 'true' : 'false');
    navItem.setAttribute('tabindex', isActive ? '0' : '-1');

    const anchor = navItem.querySelector('a');
    if (anchor) {
        anchor.setAttribute('aria-selected', isActive ? 'true' : 'false');
        anchor.setAttribute('tabindex', isActive ? '0' : '-1');
    }
};

const setPanelState = (panel, isActive) => {
    if (!panel) return;

    panel.classList.toggle('is-active', isActive);
    panel.classList.toggle('hidden', !isActive);
    panel.hidden = !isActive;
    panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
};

const activateTab = (tabsEl, targetId) => {
    if (!targetId) return;

    const panels = Array.from(tabsEl.querySelectorAll(SELECTOR_PANEL));
    const navItems = Array.from(tabsEl.querySelectorAll(SELECTOR_NAV_ITEM));

    navItems.forEach((navItem) => {
        const isActive = getTargetId(navItem) === targetId;
        setNavState(navItem, isActive);
    });

    panels.forEach((panel) => {
        const isActive = panel.id === targetId;
        setPanelState(panel, isActive);

        if (isActive) {
            panel.dispatchEvent(new CustomEvent('tab:activated', { bubbles: true }));
        }
    });
};

const initTabs = (tabsEl) => {
    const navItems = Array.from(tabsEl.querySelectorAll(SELECTOR_NAV_ITEM));
    const panels = Array.from(tabsEl.querySelectorAll(SELECTOR_PANEL));

    if (!navItems.length || !panels.length) return;

    const getPanelIdFromNav = (navItem) => {
        const targetId = getTargetId(navItem);
        const targetPanel = panels.find((panel) => panel.id === targetId);

        return targetPanel ? targetId : panels[0]?.id;
    };

    const focusNav = (navItem) => {
        if (navItem) navItem.focus({ preventScroll: true });
    };

    const changeTab = (navItem) => {
        const targetId = getPanelIdFromNav(navItem);
        activateTab(tabsEl, targetId);
    };

    navItems.forEach((navItem) => {
        navItem.addEventListener('click', (event) => {
            if (event.target.closest('a')) {
                event.preventDefault();
            }

            changeTab(navItem);
            focusNav(navItem);
        });

        navItem.addEventListener('keydown', (event) => {
            if (ACTIVATION_KEYS.includes(event.key)) {
                event.preventDefault();
                changeTab(navItem);
            }
        });
    });

    const hashTarget = window.location.hash?.slice(1);
    const hashPanel = panels.find((panel) => panel.id === hashTarget);
    const initialNav = navItems.find((item) => getPanelIdFromNav(item) === hashTarget) || navItems[0];

    if (hashPanel) {
        activateTab(tabsEl, hashTarget);
    } else {
        activateTab(tabsEl, getPanelIdFromNav(initialNav));
    }
};

export function Tabs() {
    document.querySelectorAll(SELECTOR_TABS).forEach((tabsEl) => initTabs(tabsEl));
}
